import { getCurrentInstance } from 'vue'

/**
 * The current component instance, for everything a converted component reads off `this`.
 *
 * Introduced under Vue 2.7, which had no `useRouter`/`useRoute`/`useQuasar`. Those exist now, but this
 * is deliberately still the seam: `$router`, `$route`, `$q`, `$t`/`$tc` and `$refs` are read through
 * one shim in ~200 components, and swapping them for the real composables is its own change.
 *
 * Two reasons it stays the right shim rather than reaching for a global:
 *
 *  - it stays per-instance, so a spec's globalProperties mocks still apply. Switching to the global
 *    router singleton instead would silently ignore every spec's route mock.
 *  - it stays SSR-safe. `modules/routing/routingModule` is deliberately client-only, because a
 *    module-level router under SSR would hand one visitor's router to another's request.
 *
 * Call it at the top of `setup()`, never inside a callback: `getCurrentInstance()` only answers during
 * setup, and a `computed` or handler that calls it later gets null.
 *
 *   setup () {
 *     const vm = useVm()
 *     const isHere = computed(() => vm.$route.path === '/here')
 *     return { isHere }
 *   }
 */
export function useVm () {
  const instance = getCurrentInstance()

  if (instance === null) {
    throw new Error(
      'useVm: no current component instance. It has to be called synchronously inside setup(), not from ' +
      'a computed, a watcher or an event handler.'
    )
  }

  return instance.proxy
}
